// libraries
import React, { useEffect, useContext, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Box, Button } from '@mui/material'
import { CloudDownload as CloudDownloadIcon } from '@mui/icons-material'
import { useDispatch, useSelector } from 'react-redux'

// queries
import { useGetTexasXmlUploadFileLazyQuery } from 'services/reports'

// components
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import DateRangePicker from 'components/generic/form/DateRangePicker'
import Loading from 'components/generic/Loading'
import AnalyticsContainer from 'components/containers/analytics'

// helpers and utils
import { downloader, buildFilter, getToday, getYesterday, dispatchSnackbar } from 'utils/helpers'
import { useBuildReportsList } from './reportsHelper'

const SingleReportContainer = () => {
  const { id } = useParams()
  const context = useContext(ContentLayoutContext)
  const today = getToday()
  const yesterday = getYesterday()
  const methods = useForm()
  const { control, watch } = methods
  const watchTexasDPSReportRangeValue = watch('dateRange')
  const { defaultYardTimezone } = useSelector(state => state.auth)
  const [texasDPSReportFilter, setTexasDPSReportFilter] = useState(buildFilter(yesterday, today, defaultYardTimezone))
  const { canSetRangeForDPSReport } = useSelector(state => state.featureFlags?.variables || {})
  const inOrgMode = useSelector(state => state.ui.inOrgMode)
  const dispatch = useDispatch()

  useEffect(() => {
    if (watchTexasDPSReportRangeValue) {
      const startDate = new Date(watchTexasDPSReportRangeValue[0]) || getYesterday()
      const endDate = new Date(watchTexasDPSReportRangeValue[1]) || getToday()
      const newFilter = buildFilter(startDate, endDate, defaultYardTimezone)
      setTexasDPSReportFilter(newFilter)
    }
  }, [watchTexasDPSReportRangeValue, defaultYardTimezone])

  const filteredReports = useBuildReportsList()
  const report = filteredReports.find(reportObj => reportObj.id === id)
  const reportId = inOrgMode ? report?.orgLevelReportId : report?.reportId

  const [getXml, { data: xmlData, error: xmlError, isLoading: isLoadingGetXml, requestId }] =
    useGetTexasXmlUploadFileLazyQuery()

  useEffect(() => {
    if (!xmlData) return
    downloader(xmlData, 'text/plain', `${today.toISOString().split('T')[0]}.xml`)
  }, [xmlData, requestId])

  useEffect(() => {
    if (xmlError) {
      const errorMessage = JSON.parse(xmlError?.data)?.error || 'Texas DPS Report download failed'
      dispatchSnackbar(dispatch, 'error', errorMessage)
    }
  }, [xmlError])

  const onDownload = () => {
    getXml({
      filter: canSetRangeForDPSReport ? texasDPSReportFilter : `{"date":"${yesterday.toISOString().split('T')[0]}"}`,
    })
  }

  useEffect(() => {
    context.setContainerTitle(report?.name)
  }, [])

  if (isLoadingGetXml) return <Loading />
  return (
    <Box p={3} pb={0}>
      {reportId && <AnalyticsContainer reportId={reportId} customParams={{ reportType: report?.reportType }} />}

      {report?.id === 'Texas-DPS-Upload' && (
        <>
          {canSetRangeForDPSReport && (
            <DateRangePicker name="dateRange" control={control} defaultValue={[yesterday, today]} maxDate={today} />
          )}
          <Button startIcon={<CloudDownloadIcon />} onClick={onDownload} sx={{ mt: 2 }}>
            Download
          </Button>
        </>
      )}
    </Box>
  )
}

export default SingleReportContainer
